// src/components/friendship/FriendshipVibeHowItWorks.jsx
import React from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardCheck, BookOpen, Users, TrendingUp,
  ArrowRight, Sparkles, Target, Heart, UserCircle2
} from 'lucide-react';
import Card from '../shared/Card';

const FriendshipVibeHowItWorks = () => {
  const steps = [
    {
      icon: UserCircle2,
      emoji: '🤝',
      title: 'Choose Your Guide',
      description: 'Pick from 6 unique guides — each with their own style. Your guide will be with you throughout, offering personalised support that actually fits how you think.',
      duration: '1 minute',
      color: 'from-purple-500 to-pink-500',
      tagline: 'Find someone who gets you',
    },
    {
      icon: ClipboardCheck,
      emoji: '✨',
      title: 'Take the Friendship Vibe Check',
      description: 'Answer 26 questions to discover your unique friendship style across 6 archetypes. No judgement, just insights.',
      duration: '10 minutes',
      color: 'from-teal-500 to-cyan-500',
      tagline: 'Your friendship DNA unlocked',
    },
    {
      icon: BookOpen,
      emoji: '🎯',
      title: 'Learn Lifelong Skills',
      description: 'Dive into 8 interactive modules covering everything from boundary-setting to conflict resolution. Your guide helps you along the way.',
      duration: '10-15 min each',
      color: 'from-coral-500 to-pink-500',
      tagline: 'Read at your own pace',
    },
    {
      icon: Users,
      emoji: '👥',
      title: 'Start a Friend Circle',
      description: 'Do the modules with friends you already know: the same module, one question everyone answers before anyone sees an answer, then talk about it in person.',
      duration: 'Ongoing',
      color: 'from-gold-500 to-orange-500',
      tagline: 'Do it with your friends',
    },
    {
      icon: TrendingUp,
      emoji: '📊',
      title: 'Track Your Growth',
      description: 'Monitor your progress over time. See how your patterns shift, skills improve, and friendships transform.',
      duration: 'Weekly check-ins',
      color: 'from-indigo-500 to-violet-500',
      tagline: 'Watch yourself evolve',
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-64 h-64 bg-teal-500/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-64 h-64 bg-coral-500/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-block mb-4"
          >
            <div className="text-6xl mb-4">🗺️</div>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            How It Works
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Five simple steps to transform your friendship patterns and build the social life you actually want.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="max-w-6xl mx-auto space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <Card className="p-6 md:p-8 bg-gradient-to-br from-white/5 to-white/10 border-teal-500/20 hover-lift relative overflow-hidden">
                {/* Step number badge */}
                <div className="absolute top-4 right-4 md:top-6 md:right-6">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                    {index + 1}
                  </div>
                </div>

                {/* Background decoration */}
                <div className={`absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-br ${step.color} opacity-10 rounded-full blur-2xl`}></div>

                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center relative">
                  {/* Icon section */}
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-xl flex-shrink-0`}
                  >
                    <step.icon className="w-10 h-10 text-white" />
                  </motion.div>

                  {/* Content section */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-2xl md:text-3xl font-bold text-white">
                        {step.title}
                      </h3>
                      <span className="text-3xl">{step.emoji}</span>
                    </div>

                    <p className="text-slate-300 text-lg leading-relaxed">
                      {step.description}
                    </p>

                    <div className="flex items-center gap-6 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-yellow-400" />
                        <span className="text-teal-300 font-medium italic">
                          {step.tagline}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                        <span>{step.duration}</span>
                      </div>
                    </div>
                  </div>

                  {/* Arrow indicator (except for last step) */}
                  {index < steps.length - 1 && (
                    <motion.div
                      animate={{ y: [0, 8, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      className="hidden md:block"
                    >
                      <ArrowRight className="w-8 h-8 text-teal-400/50" />
                    </motion.div>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-teal-900/40 to-coral-900/40 border border-teal-500/30 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Start Your Journey? 💜
            </h3>
            <p className="text-teal-200 mb-6">
              Better friendships, built on understanding yourself and practising the skills, at your own pace.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-red-400" />
                <span>Start instantly</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-teal-400" />
                <span>No email required</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span>100% free</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FriendshipVibeHowItWorks;
